/**
 * Single source of truth for metric definitions, plain-English descriptions,
 * "general ranges" and their sourcing.
 *
 * Drives both:
 *  - the layman's-terms tooltips (ℹ️) on the management hub, and
 *  - the RAG colour shown on gauges / stat cards.
 *
 * Sourcing approach (signed off 2026-06-04): HYBRID.
 *  - Metrics with a recognised institutional benchmark (sleep duration,
 *    activity, resting HR, calories) cite that body (NHS / BHF) directly.
 *  - WHOOP-proprietary scores (Recovery %, Strain, Sleep Performance %) are
 *    labelled "Peak4 bands, informed by WHOOP" — they are NOT presented as
 *    WHOOP's own published thresholds, because WHOOP's real bands differ
 *    (e.g. WHOOP Recovery is 3 zones ≥67/34/≤33; WHOOP publishes no Sleep
 *    Performance bands; WHOOP strain bands are Light 0-9 / Moderate 10-13 /
 *    High 14-17 / All Out 18-21).
 *  - HRV intentionally has NO fixed band: HRV has no valid universal "normal"
 *    range and is interpreted against an individual baseline.
 */

/** RAG colour used for the gauge ring / chip. `undefined` = neutral (no judgement). */
export type RagColor = "green" | "amber" | "red";

export interface MetricBand {
  /** Plain label shown to users, e.g. "Good", "Excellent", "High strain". */
  label: string;
  /** Human-readable range, e.g. "67–100%", "7–9 hrs", "≤ 15". */
  range: string;
  /** RAG colour this band maps to on the gauge. Omit for neutral/informational bands. */
  ragColor?: RagColor;
}

export interface MetricSource {
  /** Display label, e.g. "NHS", "British Heart Foundation", "Peak4 (informed by WHOOP)". */
  label: string;
  /** Canonical URL for the guidance. Omit when the band is purely Peak4-defined. */
  url?: string;
}

export interface MetricDefinition {
  /** Friendly name in plain English, e.g. "Recovery", "Resting heart rate". */
  plainName: string;
  /** One or two sentences explaining what the metric is, in layman's terms. */
  definition: string;
  /** General ranges, ordered best→worst (or low→high for scales). */
  bands: MetricBand[];
  /** Where the ranges come from. */
  source: MetricSource;
  /**
   * For numeric thresholding (gauge colour). Given a value, returns the band's
   * ragColor, or undefined when the metric carries no judgement (e.g. HRV).
   */
  ragForValue?: (value: number) => RagColor | undefined;
}

const WHOOP_INFORMED = "Peak4 wellbeing bands (informed by WHOOP)";

/**
 * Definitions keyed by the canonical metricId used across the metrics API.
 * Aliases (e.g. `recovery` → `recovery_score`) are resolved in getMetricDefinition.
 */
export const METRIC_DEFINITIONS: Record<string, MetricDefinition> = {
  recovery_score: {
    plainName: "Recovery",
    definition:
      "A 0–100% score of how ready the body is to take on strain, based mainly on overnight heart-rate variability, resting heart rate and sleep. Higher means better recovered.",
    bands: [
      { label: "Good", range: "67–100%", ragColor: "green" },
      { label: "Warning", range: "34–66%", ragColor: "amber" },
      { label: "Poor", range: "0–33%", ragColor: "red" },
    ],
    source: { label: WHOOP_INFORMED },
    ragForValue: (v) => (v >= 67 ? "green" : v >= 34 ? "amber" : "red"),
  },

  sleep_performance: {
    plainName: "Sleep performance",
    definition:
      "How much sleep employees got compared with how much their bodies needed, shown as a percentage. 100% means the full sleep need was met.",
    bands: [
      { label: "Good", range: "≥ 85%", ragColor: "green" },
      { label: "Fair", range: "70–84%", ragColor: "amber" },
      { label: "Poor", range: "Below 70%", ragColor: "red" },
    ],
    source: { label: WHOOP_INFORMED },
    ragForValue: (v) => (v >= 85 ? "green" : v >= 70 ? "amber" : "red"),
  },

  strain: {
    plainName: "Strain",
    definition:
      "A 0–21 measure of the day's overall physical and mental load (cardiovascular effort). Lower indicates a manageable, balanced workload; higher days may need recovery attention.",
    bands: [
      { label: "Low–Moderate", range: "0–13", ragColor: "green" },
      { label: "High", range: "14–17", ragColor: "amber" },
      { label: "All-out", range: "18–21", ragColor: "red" },
    ],
    source: { label: WHOOP_INFORMED },
    // Load view: low–moderate is good, high is a warning, all-out is a concern.
    ragForValue: (v) => (v <= 13 ? "green" : v <= 17 ? "amber" : "red"),
  },

  hours_of_sleep: {
    plainName: "Hours of sleep",
    definition:
      "The average number of hours slept per night. The NHS advises most adults need 7 to 9 hours a night.",
    bands: [
      { label: "Recommended", range: "7–9 hrs", ragColor: "green" },
      { label: "Slightly short", range: "6–7 hrs", ragColor: "amber" },
      { label: "Too little", range: "Below 6 hrs", ragColor: "red" },
    ],
    source: {
      label: "NHS",
      url: "https://www.nhs.uk/conditions/insomnia/",
    },
    ragForValue: (v) => (v >= 7 ? "green" : v >= 6 ? "amber" : "red"),
  },

  resting_heart_rate: {
    plainName: "Resting heart rate",
    definition:
      "The number of heartbeats per minute while at rest. A normal adult resting heart rate is 60–100 bpm; fitter people are often lower.",
    bands: [
      { label: "Healthy", range: "60–70 bpm", ragColor: "green" },
      { label: "Slightly raised", range: "71–80 bpm", ragColor: "amber" },
      { label: "Raised", range: "Above 80 bpm", ragColor: "red" },
    ],
    source: {
      label: "British Heart Foundation",
      url: "https://www.bhf.org.uk/informationsupport/how-a-healthy-heart-works/your-heart-rate",
    },
    ragForValue: (v) => (v <= 70 ? "green" : v <= 80 ? "amber" : "red"),
  },

  workout_time: {
    plainName: "Workout time",
    definition:
      "Average time spent in structured exercise. NHS guidance is at least 150 minutes of moderate activity a week (around 30 minutes on 5 days), plus muscle-strengthening on 2 days.",
    bands: [
      { label: "Meeting guidance", range: "≥ 30 min/day equiv.", ragColor: "green" },
      { label: "Some activity", range: "Below guidance", ragColor: "amber" },
      { label: "Inactive", range: "Little / none", ragColor: "red" },
    ],
    source: {
      label: "NHS physical activity guidelines",
      url: "https://www.nhs.uk/live-well/exercise/exercise-guidelines/physical-activity-guidelines-for-adults-aged-19-to-64/",
    },
  },

  calories_burn: {
    plainName: "Calories burned",
    definition:
      "Average energy expended, in kilocalories. As a reference, NHS daily intake guidance is around 2,000 kcal for women and 2,500 kcal for men.",
    bands: [],
    source: {
      label: "NHS Eatwell Guide (reference intakes)",
      url: "https://www.nhs.uk/live-well/eat-well/food-guidelines-and-food-labels/the-eatwell-guide/",
    },
  },

  hrv: {
    plainName: "Heart rate variability (HRV)",
    definition:
      "The variation in time between heartbeats, a marker of recovery and nervous-system balance. HRV is highly individual — there is no single 'normal' value, so it is read against each person's own baseline rather than a fixed target.",
    bands: [
      { label: "Compared against the individual's own baseline", range: "No fixed range" },
    ],
    source: {
      label: "Interpreted vs individual baseline (Shaffer & Ginsberg, 2017)",
      url: "https://www.frontiersin.org/articles/10.3389/fpubh.2017.00258/full",
    },
    // No fixed band — HRV carries no universal RAG judgement.
    ragForValue: () => undefined,
  },

  nap_duration: {
    plainName: "Nap duration",
    definition:
      "Average length of daytime sleep. Naps add to total daily sleep; short naps (under about 30 minutes) are generally recommended to avoid grogginess.",
    bands: [],
    source: { label: "Informational" },
  },

  sleep_efficiency: {
    plainName: "Sleep efficiency",
    definition:
      "The percentage of time in bed that was actually spent asleep. Higher means less time lying awake. Around 90% or more is widely regarded as good-quality sleep.",
    bands: [
      { label: "Good", range: "≥ 90%", ragColor: "green" },
      { label: "Fair", range: "80–89%", ragColor: "amber" },
      { label: "Poor", range: "Below 80%", ragColor: "red" },
    ],
    source: { label: "Sleep Foundation (general guideline)" },
    ragForValue: (v) => (v >= 90 ? "green" : v >= 80 ? "amber" : "red"),
  },

  sleep_consistency: {
    plainName: "Sleep consistency",
    definition:
      "How regular sleep and wake times are from day to day, shown as a percentage. More regular sleep timing is generally better.",
    bands: [
      { label: "Good", range: "≥ 80%", ragColor: "green" },
      { label: "Fair", range: "70–79%", ragColor: "amber" },
      { label: "Poor", range: "Below 70%", ragColor: "red" },
    ],
    source: { label: WHOOP_INFORMED },
    ragForValue: (v) => (v >= 80 ? "green" : v >= 70 ? "amber" : "red"),
  },

  sleep_debt: {
    plainName: "Sleep debt",
    definition:
      "The running shortfall between the sleep the body needed and the sleep actually obtained. Lower is better; it is measured relative to each person's own sleep need rather than a fixed target.",
    bands: [],
    source: { label: "Informational (vs individual need)" },
  },

  sleep_stress: {
    plainName: "Sleep stress",
    definition:
      "An estimate of how restless or disturbed sleep was, based on the share of the night spent awake or unsettled. Lower indicates more restful sleep.",
    bands: [],
    source: { label: "Informational" },
  },

  high_sleep_stress: {
    plainName: "High sleep stress",
    definition:
      "The proportion of sleep sessions flagged as restless or disturbed (more time spent awake than usual). Lower is better.",
    bands: [],
    source: { label: "Informational" },
  },

  sleep_stage_rem: {
    plainName: "REM sleep",
    definition:
      "Average time spent in REM (dreaming) sleep, which supports memory and mood. In healthy adults REM is typically around a quarter of the night.",
    bands: [{ label: "Typical for adults", range: "~20–25% of sleep" }],
    source: { label: "NHS Every Mind Matters (sleep)" },
  },

  sleep_stage_deep: {
    plainName: "Deep sleep",
    definition:
      "Average time spent in deep (slow-wave) sleep, the most physically restorative stage. In healthy adults this is typically around a quarter of the night and naturally declines with age.",
    bands: [{ label: "Typical for adults", range: "~15–25% of sleep" }],
    source: { label: "NHS Every Mind Matters (sleep)" },
  },

  sleep_stage_light: {
    plainName: "Light sleep",
    definition:
      "Average time spent in light sleep, the largest part of a normal night and the stage that links the deeper stages together.",
    bands: [{ label: "Typical for adults", range: "~45–55% of sleep" }],
    source: { label: "Informational" },
  },

  sleep_stage_awake: {
    plainName: "Awake time",
    definition:
      "Average time spent awake while in bed during the night. Brief awakenings are normal; consistently high awake time can point to disturbed sleep.",
    bands: [],
    source: { label: "Informational" },
  },

  respiratory_rate: {
    plainName: "Respiratory rate",
    definition:
      "The number of breaths taken per minute at rest. A normal resting rate for adults is 12–20 breaths per minute.",
    bands: [
      { label: "Normal", range: "12–20 breaths/min", ragColor: "green" },
      { label: "Outside normal range", range: "< 12 or > 20", ragColor: "red" },
    ],
    source: { label: "NHS (clinical normal range)" },
    ragForValue: (v) => (v >= 12 && v <= 20 ? "green" : "red"),
  },

  spo2: {
    plainName: "Blood oxygen (SpO2)",
    definition:
      "The percentage of oxygen carried in the blood. A normal level for a healthy adult is 95–100%. This is a wellbeing indicator, not a medical reading.",
    bands: [
      { label: "Normal", range: "95–100%", ragColor: "green" },
      { label: "Low", range: "93–94%", ragColor: "amber" },
      { label: "Concern", range: "Below 93%", ragColor: "red" },
    ],
    source: { label: "NHS (normal oxygen levels)" },
    ragForValue: (v) => (v >= 95 ? "green" : v >= 93 ? "amber" : "red"),
  },

  temperature: {
    plainName: "Skin temperature",
    definition:
      "Overnight skin temperature shown as the difference from the person's own typical baseline, rather than an absolute reading. Large shifts can accompany illness or poor sleep.",
    bands: [],
    source: { label: "Informational (vs individual baseline)" },
  },

  stress_score: {
    plainName: "Stress score",
    definition:
      "A real-time estimate of physiological stress, based mainly on heart rate and heart-rate variability. It is shown as low / medium / high zones; there is no clinical standard, so treat it as a wellbeing indicator.",
    bands: [],
    source: { label: "Informational (device product metric)" },
  },

  high_stress_days: {
    plainName: "High-stress days",
    definition:
      "The number of days in the period where average physiological stress was classed as high. Fewer is generally better.",
    bands: [],
    source: { label: "Informational" },
  },

  stress_recovery_balance: {
    plainName: "Stress–recovery balance",
    definition:
      "How well recovery is keeping pace with accumulated stress. A higher balance suggests stress and recovery are well matched.",
    bands: [],
    source: { label: "Informational" },
  },

  steps: {
    plainName: "Steps",
    definition:
      "Average estimated steps per day. There is no official step target — the well-known '10,000 steps' began as a marketing slogan — but research suggests health benefits build up to roughly 7,000–8,000 steps a day. The NHS instead promotes brisk 10-minute walks ('Active 10').",
    bands: [
      { label: "Active", range: "≥ 8,000 / day", ragColor: "green" },
      { label: "Moderately active", range: "5,000–7,999", ragColor: "amber" },
      { label: "Low", range: "Below 5,000", ragColor: "red" },
    ],
    source: {
      label: "NHS (Active 10) — research-informed, not an official target",
      url: "https://www.nhs.uk/live-well/exercise/physical-activity-guidelines-for-adults-aged-19-to-64/",
    },
    ragForValue: (v) => (v >= 8000 ? "green" : v >= 5000 ? "amber" : "red"),
  },

  active_time: {
    plainName: "Active time",
    definition:
      "Average time spent in moderate-or-harder activity. NHS guidance is at least 150 minutes of moderate activity a week — roughly 30 minutes on 5 days.",
    bands: [
      { label: "Meeting guidance", range: "≥ 30 min/day equiv.", ragColor: "green" },
      { label: "Some activity", range: "Below guidance", ragColor: "amber" },
      { label: "Inactive", range: "Little / none", ragColor: "red" },
    ],
    source: {
      label: "NHS physical activity guidelines",
      url: "https://www.nhs.uk/live-well/exercise/exercise-guidelines/physical-activity-guidelines-for-adults-aged-19-to-64/",
    },
  },

  cardio_load: {
    plainName: "Cardio load",
    definition:
      "An estimate of the energy the heart and body expended, in kilojoules. It depends heavily on body size, age and activity, so it is best read as a trend over time rather than against a fixed target.",
    bands: [],
    source: { label: "Informational (vs individual trend)" },
  },

  heart_rate: {
    plainName: "Heart rate",
    definition:
      "Average heartbeats per minute. Resting heart rate for most adults sits between 60 and 100 bpm; it naturally rises with activity and stress.",
    bands: [],
    source: {
      label: "British Heart Foundation",
      url: "https://www.bhf.org.uk/informationsupport/how-a-healthy-heart-works/your-heart-rate",
    },
  },

  hours_of_sleep_needed: {
    plainName: "Hours of sleep needed",
    definition:
      "An estimate of how much sleep the body needed, used as the benchmark for sleep performance. For most adults the NHS recommends 7–9 hours a night.",
    bands: [],
    source: {
      label: "NHS",
      url: "https://www.nhs.uk/conditions/insomnia/",
    },
  },

  day_strain: {
    plainName: "Day strain",
    definition:
      "A 0–21 measure of the day's overall physical and mental load. Higher numbers mean a more demanding day.",
    bands: [
      { label: "Low–Moderate", range: "0–13", ragColor: "green" },
      { label: "High", range: "14–17", ragColor: "amber" },
      { label: "All-out", range: "18–21", ragColor: "red" },
    ],
    source: { label: WHOOP_INFORMED },
    ragForValue: (v) => (v <= 13 ? "green" : v <= 17 ? "amber" : "red"),
  },
};

/** Aliases → canonical metricId. */
const METRIC_ALIASES: Record<string, string> = {
  recovery: "recovery_score",
  sleep: "sleep_performance",
  average_hrv: "hrv",
  avg_heart_rate: "heart_rate",
  max_heart_rate: "heart_rate",
};

/**
 * Loose display-label → metricId fallback, for components that only have the
 * series label (e.g. chart hover tooltips). ORDER MATTERS: most specific first,
 * so "resting heart rate" wins over "heart rate" and "deep sleep" over "sleep".
 */
const LABEL_KEYWORD_MAP: Array<{ keyword: string; metricId: string }> = [
  // Sleep stages (before generic "sleep")
  { keyword: "rem", metricId: "sleep_stage_rem" },
  { keyword: "deep", metricId: "sleep_stage_deep" },
  { keyword: "slow wave", metricId: "sleep_stage_deep" },
  { keyword: "light sleep", metricId: "sleep_stage_light" },
  { keyword: "awake", metricId: "sleep_stage_awake" },
  // Sleep detail metrics
  { keyword: "sleep performance", metricId: "sleep_performance" },
  { keyword: "sleep efficiency", metricId: "sleep_efficiency" },
  { keyword: "efficiency", metricId: "sleep_efficiency" },
  { keyword: "sleep consistency", metricId: "sleep_consistency" },
  { keyword: "sleep debt", metricId: "sleep_debt" },
  { keyword: "sleep stress", metricId: "sleep_stress" },
  { keyword: "hours of sleep needed", metricId: "hours_of_sleep_needed" },
  { keyword: "hours of sleep", metricId: "hours_of_sleep" },
  { keyword: "total sleep", metricId: "hours_of_sleep" },
  { keyword: "nap", metricId: "nap_duration" },
  // Heart-rate family (resting/max before generic heart rate)
  { keyword: "resting", metricId: "resting_heart_rate" },
  { keyword: "max h", metricId: "heart_rate" },
  { keyword: "hrv", metricId: "hrv" },
  { keyword: "heart rate variability", metricId: "hrv" },
  { keyword: "heart rate", metricId: "heart_rate" },
  // Vitals
  { keyword: "respiratory", metricId: "respiratory_rate" },
  { keyword: "spo2", metricId: "spo2" },
  { keyword: "blood oxygen", metricId: "spo2" },
  { keyword: "temperature", metricId: "temperature" },
  // Strain & activity
  { keyword: "day strain", metricId: "day_strain" },
  { keyword: "strain", metricId: "strain" },
  { keyword: "cardio load", metricId: "cardio_load" },
  { keyword: "active time", metricId: "active_time" },
  { keyword: "workout", metricId: "workout_time" },
  { keyword: "step", metricId: "steps" },
  { keyword: "calorie", metricId: "calories_burn" },
  // Stress / mind
  { keyword: "stress recovery", metricId: "stress_recovery_balance" },
  { keyword: "stress-recovery", metricId: "stress_recovery_balance" },
  { keyword: "high stress", metricId: "high_stress_days" },
  { keyword: "stress", metricId: "stress_score" },
  // Recovery (kept late so "stress recovery" matches first)
  { keyword: "recovery", metricId: "recovery_score" },
  // Engagement
  { keyword: "participation", metricId: "participation_rate" },
  { keyword: "completion", metricId: "completion_rate" },
];

/**
 * Maps hyphenated UI toggle tokens (used on the Team/User Progress and Sleep
 * trend switches) to canonical metricIds, so toggle-driven chart tooltips can
 * resolve a definition. Returns the input unchanged when already canonical.
 */
const TOGGLE_TOKEN_MAP: Record<string, string> = {
  "heart-rate": "heart_rate",
  "heart-rate-variability": "hrv",
  "resting-hr": "resting_heart_rate",
  "resting-heart-rate": "resting_heart_rate",
  "respiratory-rate": "respiratory_rate",
  "recovery-score": "recovery_score",
  "sleep-stages": "sleep_stage_deep",
  "total-sleep": "hours_of_sleep",
  efficiency: "sleep_efficiency",
  "sleep-debt": "sleep_debt",
  "sleep-stress": "sleep_stress",
  strain: "strain",
  calories: "calories_burn",
};

export const toMetricKey = (token: string | undefined): string =>
  (token && TOGGLE_TOKEN_MAP[token]) || token || "";

/**
 * Resolve a token/label to its canonical metricId using the same precedence as
 * getMetricDefinition (exact key → alias → toggle token → label keyword).
 * Returns undefined when nothing matches.
 */
export const resolveMetricId = (
  metricIdOrLabel: string | undefined
): string | undefined => {
  if (!metricIdOrLabel) return undefined;
  const key = metricIdOrLabel.toLowerCase().trim();

  if (METRIC_DEFINITIONS[key]) return key;
  if (METRIC_ALIASES[key]) return METRIC_ALIASES[key];
  if (TOGGLE_TOKEN_MAP[key]) return TOGGLE_TOKEN_MAP[key];

  const match = LABEL_KEYWORD_MAP.find((m) => key.includes(m.keyword));
  return match ? match.metricId : undefined;
};

/** Resolve a definition by metricId (preferred) or, failing that, by display label. */
export const getMetricDefinition = (
  metricIdOrLabel: string | undefined
): MetricDefinition | undefined => {
  const id = resolveMetricId(metricIdOrLabel);
  return id ? METRIC_DEFINITIONS[id] : undefined;
};

/**
 * Unit of measure per canonical metricId, for chart Y-axis labels and tooltip
 * value suffixes (BT-218). Only metrics with a meaningful unit are listed;
 * scale-only metrics (e.g. strain 0–21, stress score, step counts) and
 * baseline-relative metrics (HRV, skin temperature) are intentionally omitted
 * so we don't imply a unit they don't have.
 */
const METRIC_UNITS: Record<string, string> = {
  recovery_score: "%",
  sleep_performance: "%",
  sleep_efficiency: "%",
  sleep_consistency: "%",
  high_sleep_stress: "%",
  hours_of_sleep: "hrs",
  hours_of_sleep_needed: "hrs",
  sleep_debt: "hrs",
  nap_duration: "hrs",
  sleep_stage_rem: "hrs",
  sleep_stage_deep: "hrs",
  sleep_stage_light: "hrs",
  sleep_stage_awake: "hrs",
  resting_heart_rate: "bpm",
  heart_rate: "bpm",
  respiratory_rate: "br/min",
  spo2: "%",
  calories_burn: "kcal",
  cardio_load: "kJ",
  workout_time: "min",
  active_time: "min",
  high_stress_days: "days",
};

/**
 * Returns the unit string for a metricId or display label (e.g. "%", "bpm",
 * "hrs"), or "" when the metric has no meaningful unit.
 */
export const getMetricUnit = (metricIdOrLabel: string | undefined): string => {
  const id = resolveMetricId(metricIdOrLabel);
  return (id && METRIC_UNITS[id]) || "";
};

export interface MetricRagStatus {
  /** "Good" | "Warning" | "Monitor", or undefined when the metric has no band. */
  label?: string;
  /** RAG colour for the value, or undefined when the metric carries no judgement. */
  ragColor?: RagColor;
}

const RAG_STATUS_LABEL: Record<RagColor, string> = {
  green: "Good",
  amber: "Warning",
  red: "Monitor",
};

/**
 * Given a metricId-or-label and a value, returns the RAG band that value falls
 * into (colour + label). Returns empty when the metric has no agreed band
 * (e.g. HRV) so callers can show the value without a judgement.
 */
export const getMetricRagStatus = (
  metricIdOrLabel: string | undefined,
  value: number | null | undefined
): MetricRagStatus => {
  if (value == null || Number.isNaN(value)) return {};
  const def = getMetricDefinition(metricIdOrLabel);
  const ragColor = def?.ragForValue?.(value);
  if (!ragColor) return {};
  return { ragColor, label: RAG_STATUS_LABEL[ragColor] };
};
